<template>
    <page-title
        title="アプリ申し込み"
        icon="bi-clipboard-plus"
        :backTo="{ name: 'PageSchoolList' }"
    >
    </page-title>

    <inline-loader v-if="loading" />

    <div v-else>
        <summary-box title="学校">
            <summary-box-row :text="this.school.school_name"/>
            <summary-box-row :text="'（' + this.school.full_address + '）'"/>
        </summary-box>

        <form class="mt-3" @submit.prevent="$refs.confirm_save.show();">
            <div class="row mb-4">
                <div class="col-8 col-md-6">
                    <label class="form-label">年度</label>
                    <form-select
                        v-model="order.year"
                        :options="option_years"
                        :required=true
                        @change="getAppList"
                    ></form-select>
                </div>
                <div class="col-10 col-md-6">
                    <label class="form-label">アプリ種類</label>
                    <form-select
                        v-model="order.app_key"
                        :options="apps"
                        option_key="app_key"
                        option_label="app_name"
                        :empty_option="app_list_loading ? '読込中...' : '--選択--'"
                        :required=true
                        model_type="string"
                        @change="changeShippingFeeAndUnitPrice()"
                    ></form-select>
                </div>
            </div>

            <hr class="mb-3">

            <div class="row mt-md-3" v-for="(detail, index) in order.details" :key="index">
                <div class="col-8 col-md-4">
                    <label class="form-label">学年</label>
                    <form-select
                        v-model="detail.grade"
                        :options="grades"
                        empty_option="--選択--"
                        :required="index === 0"
                    ></form-select>
                </div>
                <div class="col-16 col-md-8 row">
                    <label class="form-label">{{ displayClassType(index) }}</label>
                    <div class="col input-group">
                        <select
                            v-model="detail.school_class.school_class_id"
                            class="form-control"
                            :required="index === 0"
                        >
                            <option value="" selected>--選択--</option>
                            <template
                                v-if="order.year &&
                                detail.grade &&
                                (order.year in school_classes) &&
                                (detail.grade in school_classes[order.year])"
                            >
                                <template
                                    v-for="school_class in school_classes[order.year][detail.grade]"
                                    :key="school_class.school_class_id"
                                >
                                    <option :value="school_class.school_class_id">
                                        {{ school_class.class_name_display }}
                                    </option>
                                </template>
                            </template>
                        </select>
                        <!-- クラス追加モーダルOPENボタン -->
                        <button-dom-add
                            class="disabled_btn"
                            color="btn-outline-secondary"
                            :disabled="$helper.isNud(detail.grade)"
                            text=""
                            icon="bi-plus"
                            @click="openModalClassAdd(index)"
                        ></button-dom-add>
                    </div>
                </div>
                <div class="col-10 col-md-5 mt-2 mt-md-0">
                    <label class="form-label">人数</label>
                    <div class="input-group">
                        <input
                            v-model="detail.account_number"
                            type="number"
                            class="form-control"
                            step="1"
                            min="0"
                            :required="index === 0"
                        >
                        <span class="input-group-text">人</span>
                    </div>
                </div>
                <!-- 削除ボタン -->
                <div class="col col-md-6 align-self-end text-end text-md-start">
                    <button-dom-remove text="行削除" @click="removeRow(index)"></button-dom-remove>
                </div>

                <hr class="d-block d-md-none mt-3">
            </div>

            <!-- 行追加ボタン -->
            <div class="d-flex justify-content-center justify-content-md-end">
                <button-dom-add @click="addRow()"></button-dom-add>
            </div>

            <hr>

            <label class="form-label mt-3">担当先生</label>
            <div class="col-14 col-md-8">
                <div class="input-group">
                    <input type="text" placeholder="三和 一郎" v-model="order.recipient_name" class="form-control" required>
                    <span class="input-group-text">先生</span>
                </div>
            </div>

            <label class="form-label mt-3">学校連絡先電話番号</label>
            <div class="col-14 col-md-8">
                <div class="input-group">
                    <input type="text" placeholder="06-6789-3336" v-model="order.recipient_phone" class="form-control" pattern="^(0{1}\d{1,4}-{0,1}\d{1,4}-{0,1}\d{4})$" required>
                </div>
            </div>

            <label class="form-label mt-3">発送希望日 <span class="badge bg-secondary">任意</span></label>
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <input
                        type="date"
                        v-model="order.shipping_date"
                        class="form-control"
                        :min="min_shipping_date"
                    >
                </div>
            </div>
            <text-info class="mt-3">
                メール便のため、着日は指定できません。
                最短翌営業日の発送です。<br>
                希望日が休業日の場合、翌営業日の発送となります。
            </text-info>

            <div class="col-10 col-md-6 mt-3">
                <label class="form-label">配送方法</label>
                <form-select
                    v-model="order.shipping_method"
                    empty_option="-- 選択 --"
                    :options="shipping_methods"
                    required
                    @change="changeShippingFeeAndUnitPrice()"
                ></form-select>
            </div>
            <text-info class="mt-3">
                配送方法を「メール」にした場合、シールは発送されません。
            </text-info>

            <label class="form-label mt-3">メールアドレス</label>
            <div class="col-14 col-md-10">
                <div class="input-group">
                    <input
                        type="email"
                        placeholder="sanwa@sample.com"
                        v-model="order.email"
                        class="form-control"
                        pattern="^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$"
                        :required = "is_active"
                    >
                </div>
            </div>
            <text-info class="mt-3">
                このメールアドレス宛に、先生用のアカウント情報が通知されます。原則、先生のメールアドレスをご入力ください。
            </text-info>

            <template v-if="school.has_previous_orders">
                <label class="form-label mt-3">先生アカウント再発行</label>
                <div class="row align-items-center">
                    <div class="col-12 col-md-8">
                        <form-radio
                            v-model="regenerate_teacher_account"
                            :options="regenerate_teacher_accounts"
                            :checked="school.has_previous_orders"
                            required
                        ></form-radio>
                    </div>
                </div>
            </template>


            <text-info class="mt-3">
                <!-- テクノクラフ継続利用の学校の場合 -->
                <template v-if="school.has_previous_orders">
                    追加の先生アカウントが必要な場合は「{{ ReGenerateTeacherAccount.get(ReGenerateTeacherAccount.YES) }}」にしてください（先生アカウントが一つ発行されます）。
                </template>
                <!-- テクノクラフ初回注文の学校の場合 -->
                <template v-else>
                    先生アカウントは学年数分発行されます（テクノクラフへ初めてお申し込みの学校）。
                </template>
            </text-info>


            <template v-if="order.app_key === 'repocraft_muji' || order.app_key === 'repocraft'">
                <label class="form-label mt-3">評価機能オプション（別途 3,000円/年）</label>
                <div class="row align-items-center">
                    <div class="col-12 col-md-8">
                        <form-radio
                            v-model="order.repocraft_evaluation"
                            :options="repocraft_evaluations"
                            required
                        ></form-radio>
                    </div>
                </div>
            </template>

            <label class="form-label mt-3">備考</label>
            <textarea v-model="order.note" class="form-control"></textarea>

            <hr class="mt-4">

            <!-- 合計欄 -->
            <conclusion-row
                title="合計"
                :text="$helper.number(account_number_sum)"
                unit="ライセンス"
            ></conclusion-row>
            <conclusion-row
                title="卸価"
                :text="unit_price_text"
                unit="円／人"
            ></conclusion-row>
            <conclusion-row
                title="合計金額"
                :text="$helper.number(total_price)"
                unit="円"
            ></conclusion-row>
            <conclusion-row
                title="送料"
                :text="shipping_fee"
                unit="円"
            ></conclusion-row>
            <text-info class="mt-3">同一学校の複数学年は、１回でまとめて注文してください。複数回で注文された場合、追加の送料をいただく場合があります。</text-info>

            <!-- 申込ボタン -->
            <div class="my-5">
                <button-wrap>
                    <template v-slot:default>
                        <button-exec-edit
                            text="申し込み"
                        ></button-exec-edit>
                    </template>
                </button-wrap>
            </div>
        </form>
    </div>

<!-- クラス追加モーダル -->
<div ref="modal_add_class" v-if="modal_shown">
    <transition name="fade">
        <div v-if="modal_shown">
            <div class="modal-backdrop show"></div>
            <div class="modal" tabindex="-1" style="display:block">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h4 class="modal-title">{{ displayClassType(modal_index) }}の追加</h4>
                            <button type="button" class="btn-close" @click="closeModalClassAdd"></button>
                        </div>
                        <div class="modal-body">
                            <summary-box title="対象">
                                <summary-box-row
                                    :text="this.school.school_name"
                                />
                                <summary-box-row
                                    :text="order.details[modal_index].grade ? Grade.getWhereSchoolType(this.school.school_type, order.details[modal_index].grade) : '選択なし'"
                                />
                            </summary-box>
                            <template
                                v-if="$helper.isNud(order.details[modal_index].grade) ||
                                $helper.isNud(order.year)"
                            >
                                <text-warn class=" mt-4">
                                    {{ displayClassType(modal_index) }}を追加できません。このウインドウを閉じ、先に学年を指定してください。
                                </text-warn>
                            </template>
                            <template v-else>
                                <div class="row justify-content-center">
                                    <text-info class="col-24 text-center mt-4">
                                        追加する{{ displayClassType(modal_index) }}名を入力して追加ボタンをクリックしてください。
                                    </text-info>
                                    <!-- クラス追加フォーム -->
                                    <form @submit.prevent="submitAddClass(modal_index)">
                                        <div class="row align-items-center mb-3 mx-0 mx-md-5">
                                            <div class="col-24 col-md-12 col-lg-10 me-5">
                                                <div class="input-group">
                                                    <input
                                                        type="text"
                                                        v-model="new_class_name"
                                                        class="form-control"
                                                        required
                                                        :disabled="Number(new_class_is_singular)"
                                                    >
                                                    <span class="input-group-text" v-if="!is_club">組</span>
                                                </div>
                                            </div>
                                            <form-checkbox
                                                v-if="!is_club"
                                                v-model="new_class_is_singular"
                                                option_label="クラス名なし（単学級）"
                                                @change="changeClassNameValue()"
                                                class="col"
                                            ></form-checkbox>
                                        </div>
                                        <div class="text-center mt-4">
                                            <button-exec-edit text="追加" />
                                        </div>
                                    </form>
                                </div>
                            </template>
                        </div>
                        <div class="modal-footer">
                            <button-wrap>
                                <template v-slot:negative>
                                    <button-dom-close @click="closeModalClassAdd"></button-dom-close>
                                </template>
                            </button-wrap>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</div>

<confirm-dialog ref="confirm_save" @ok="save">
    <!-- 各クラス〇人より多ければダイアログで確認 -->
    <p v-if="has_over_warn_accounts_class" class="text-danger">申し込み人数が{{ over_warn_accounts }}人を超えるクラスがあります。</p>
    <p>申込みを確定してよろしいですか</p>
</confirm-dialog>

</template>

<script>
import Grade from '@/shared/models/enums/grade';
import ReGenerateTeacherAccount from '@/shared/models/enums/regenerate-teacher-account';
import RepocraftEvaluation from '@/shared/models/enums/repocraft-evaluation';
import ShippingMethod from '@/shared/models/enums/shipping-method';
import Order from '@/shared/models/entities/order';
import OrderDetail from '@/shared/models/entities/order-detail';
import School from '@/shared/models/entities/school';
import SchoolClass from '@/shared/models/entities/school-class';
import SubmitOrder from '@/shared/models/services/submit-order';
import FormCheckbox from '@/shared/components/form/FormCheckbox';
import FormSelect from '@/shared/components/form/FormSelect';
import FormRadio from '@/shared/components/form/FormRadio';
import PageTitle from '@/shared/components/design/PageTitle';
import ConfirmDialog from '@/shared/components/tool/ConfirmDialog';
import InlineLoader from '@/shared/components/tool/InlineLoader';
import SummaryBox from '@/shared/components/design/SummaryBox';
import SummaryBoxRow from '@/shared/components/design/SummaryBoxRow';
import ConclusionRow from '@/shared/components/design/ConclusionRow';
import TextInfo from '@/shared/components/design/TextInfo';
import TextWarn from '@/shared/components/design/TextWarn';
import ButtonDomAdd from '@/shared/components/design/buttons/ButtonDomAdd';
import ButtonDomRemove from '@/shared/components/design/buttons/ButtonDomRemove';
import ButtonExecEdit from '@/shared/components/design/buttons/ButtonExecEdit';
import ButtonDomClose from '@/shared/components/design/buttons/ButtonDomClose';
import ButtonWrap from '@/shared/components/design/buttons/ButtonWrap';


export default {
    name: 'PageSchoolOrder',
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage',
    ],
    components: {
        InlineLoader,
        ConfirmDialog,
        FormCheckbox,
        FormSelect,
        FormRadio,
        PageTitle,
        SummaryBox,
        SummaryBoxRow,
        ConclusionRow,
        TextInfo,
        TextWarn,
        ButtonDomAdd,
        ButtonDomRemove,
        ButtonExecEdit,
        ButtonDomClose,
        ButtonWrap,
    },
    data() {
        return {
            loading: 0,
            app_list_loading: false,

            //表示系
            school: new School(),
            shipping_fee: 0,
            unit_price: 0,

            //選択肢
            apps: [],
            years: SubmitOrder.orderableYears(),
            school_classes: {},
            shipping_methods: ShippingMethod.options(),
            regenerate_teacher_accounts: ReGenerateTeacherAccount.options(), //先生アカウント再発行 選択肢
            repocraft_evaluations: RepocraftEvaluation.options(), //評価機能オプション 選択肢

            //送信データ
            order: new Order(),
            regenerate_teacher_account: ReGenerateTeacherAccount.NO, //先生アカウント再発行初期値

            //モーダル
            modal_shown: false,
            new_class_name: '',
            new_class_is_singular: '0',
            modal_index: null,
            Grade: Grade,

            //enum
            ReGenerateTeacherAccount: ReGenerateTeacherAccount, //先生アカウント再発行
            //注意ダイアログ文を出す、クラスの申し込み人数〇人以上
            over_warn_accounts: 40,
        }
    },
    mounted() {
        //空のdetailを３つ代入（初期は３行表示しておく）
        this.pushThreeOrderDetailsForInit();

        //orderに年度を設定
        this.order.year = SubmitOrder.orderableYears()[0];

        //orderに評価機能オプション初期値を設定
        this.order.repocraft_evaluation = RepocraftEvaluation.OFF; //評価機能オプション初期値

        //アプリの選択肢を設定
        this.getAppList();

        //学校情報を取得
        this.fetchSchool();

        // クラス情報を取得
        this.fetchSchoolClasses();
    },
    computed: {
        is_club() {
            if (
                this.order.details[this.modal_index].grade === Grade.ESC
                || this.order.details[this.modal_index].grade === Grade.JHC
            ) {
                return true;
            }

            return false
        },
        //発送希望日を翌日より選べるようにする
        min_shipping_date() {
            //今日の日付
            let today = new Date();
            //翌日の日付
            let tomorrow = today.setDate(today.getDate() + 1);

            //yyyy-mm-ddの形にして返す
            return this.$helper.ymdForInputDate(tomorrow);
        },
        //発送方法を3(メール)を選んだ時のみ、メールアドレスの入力を必須にする。
        is_active() {
            return this.order.shipping_method === ShippingMethod.EMAIL;
        },
        grades() {
            return Grade.optionsWhereSchoolType(this.school.school_type);
        },
        //児童の人数を合計を表示
        account_number_sum() {
            let total = 0;
            for (let detail of this.order.details) {
                if (this.$helper.isNud(detail.account_number)) {
                    continue;
                }
                total += parseInt(detail.account_number);
            }
            return total;
        },
        //〇人を超える申し込み人数のクラスがあるか
        has_over_warn_accounts_class() {
            for (let detail of this.order.details) {
                if (this.$helper.isNud(detail.account_number)) {
                    continue;
                }
                if (parseInt(detail.account_number) > this.over_warn_accounts) {
                    return true;
                }
            }
            return false;
        },
        //申込みの合計金額を表示
        total_price() {
            if (this.unit_price === null) {
                return 0;
            }
            return this.account_number_sum * this.unit_price;
        },
        //年度の配列をオブジェクトの配列に変換
        option_years() {
            return this.years.map(year => ({key: year, label: year + '年度'}));
        },
        // 卸価の表示（年額 or 月額×月数の内訳）
        unit_price_breakdown_text() {
            const unit = parseInt(this.unit_price || 0, 10);
            // 表示基準の開始月：申込作業時点の月
            const baseDate = new Date();
            const startMonth = baseDate.getMonth() + 1; // 1-12
            // 年度末(3月)までの残月数を計算
            let months;
            if (startMonth >= 4) {
                months = 12 - startMonth + 4; // 4..12月 → 翌年3月まで
            } else {
                months = 3 - startMonth + 1;  // 1..3月 → 当年3月まで
            }
            // 年額期間 4〜7月は年額表示
            if (startMonth >= 4 && startMonth <= 7) {
                return this.$helper.number(unit);
            }
            if (months <= 0) {
                return this.$helper.number(unit);
            }
            const monthly = Math.round(unit / months);
            return `${this.$helper.number(monthly)}円×${months}（か月）${this.$helper.number(unit)}`;
        },
        // アプリ別の卸価表示を振り分け（repocraft 系のみ内訳表示）
        unit_price_text() {
            const key = this.order?.app_key;
            if (key === 'repocraft' || key === 'repocraft_muji') {
                return this.unit_price_breakdown_text;
            }
            return this.$helper.number(this.unit_price);
        },
    },
    methods: {
        //空のdetailを３つ代入（初期は３行表示しておく）
        pushThreeOrderDetailsForInit() {
            for (let i = 1; i <= 3; i++) {
                this.order.details.push(new OrderDetail());
            }
        },
        //アプリリストの取得
        getAppList() {
            this.apps = [];

            if (this.order.year === null) {
                return;
            }

            //select用アプリ一覧を取得
            this.app_list_loading = true;
            this.$http.get('/distributor/app', {
                params: {year: this.order.year}
            })
            .then(response => {
                this.apps = response.data;
            }).finally(() => {
                this.app_list_loading = false;
            });
        },
        // 学校情報を取得
        fetchSchool() {
            this.loading++;

            this.$http.get('/distributor/school/' + parseInt(this.$route.params.school_id, 10))
            .then(response => {
                this.school = new School(response.data);
                //Order 初期値
                this.order.school = this.school;
            })
            .finally(() => {
                this.loading--;
            });
        },
        //クラス情報を取得
        fetchSchoolClasses() {
            for (let year of this.years) {
                this.loading++;

                this.school_classes[year] = {};
                this.$http.get(`/distributor/school/${parseInt(this.$route.params.school_id, 10)}/year/${year}`)
                .then(response => {
                    let grades = response.data.classes;

                    for (let grade in grades) {
                        this.school_classes[year][grade] = [];

                        for (let school_class of grades[grade]) {
                            this.school_classes[year][grade].push(new SchoolClass(school_class));
                        }
                    }
                })
                .finally(() => {
                    this.loading--;
                });
            }
        },
        // クラス or クラブ活動 or 部活動
        displayClassType(index) {
            if (this.order.details[index].grade === Grade.ESC ) {
                return 'クラブ活動';
            }

            if (this.order.details[index].grade === Grade.JHC) {
                return '部活動';
            }

            return 'クラス';
        },
        //選ばれたアプリ・配送方法の送料、卸価を表示
        changeShippingFeeAndUnitPrice() {
            this.shipping_fee = "読込中...";
            if (this.order.app_key === null) {
                this.shipping_fee = null;
            }
            if (this.order.shipping_method === null) {
                this.shipping_fee = null;
            }
            if (this.order.app_key && this.order.shipping_method) {
                // 指定のapp_keyと配送方法で、配送料を取得
                this.$http.get(`/distributor/shipping_fee/app_key/${this.order.app_key}/shipping_method/${this.order.shipping_method}`, {
                    params: {year: this.order.year}
                })
                .then(response => {
                    this.shipping_fee = response.data.shipping_fee;
                    this.unit_price = response.data.unit_price;
                });
            }
        },
        //申込送信
        save() {
            this.startScreenLoading();

            // 入力されていないdetailは省く
            this.removeEmptyOrderDetails();

            //レポクラフト・レポクラフト無地以外を選択している場合
            if (this.order.app_key !== 'repocraft_muji' && this.order.app_key !== 'repocraft') {
                this.order.repocraft_evaluation = null;
            }

            this.$http.post('/distributor/order', {
                order: this.order,
                regenerate_teacher_account: this.regenerate_teacher_account,
            })
            .then(response => {
                //リダイレクトする
                this.showMessage('登録しました');
                this.$router.push('/distributor/order/' + response.data.order_id);
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        // 学年選択の空行を取り除いて詰め直す
        removeEmptyOrderDetails() {
            let filled_details = this.order.details.filter(detail => {
                return (
                    detail.grade
                    && detail.school_class.school_class_id
                    && detail.account_number > 0
                );
            });
            this.order.details.splice(0);
            this.order.details.push(...filled_details);
        },
        //クラス追加
        submitAddClass(index) {
            this.startScreenLoading();
            this.$http.post(`/distributor/school/${parseInt(this.$route.params.school_id, 10)}/class`, {
                year: this.order.year,
                grade: this.order.details[this.modal_index].grade,
                class_name: this.new_class_name,
                is_singular: this.new_class_is_singular,
            })
            .then(response => {
                this.closeModalClassAdd();
                //クラス情報に追加
                let new_school_class = new SchoolClass(response.data);

                if (
                    !Array.isArray(this.school_classes[new_school_class.year][new_school_class.grade])
                ) {
                    this.school_classes[new_school_class.year][new_school_class.grade] = [];
                }
                this.school_classes[new_school_class.year][new_school_class.grade].push(new_school_class);

                this.order.details[index].school_class.school_class_id = new_school_class.school_class_id;
                this.showMessage('クラスを追加しました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        //単学級選択時
        changeClassNameValue() {
            if (Number(this.new_class_is_singular)) {
                this.new_class_name = '';
            }
        },
        //行追加
        addRow() {
            this.order.details.push(new OrderDetail());
        },
        //行削除
        removeRow(index) {
            this.order.details.splice(index, 1);
        },
        openModalClassAdd(index) {
            this.modal_index = index; //クラス作成後に注文のクラスIDをセットするため
            this.new_class_name = '';
            this.new_class_is_singular = '0';
            this.modal_shown = true;
        },
        closeModalClassAdd() {
            this.modal_shown = false;
        },
    },

}
</script>

<style scoped>
    @media (min-width: 900px) {
        .modal-dialog { max-width: 800px; margin: 1.75rem auto; }
    }
</style>
